import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { OtherExpense, OtherExpenseFilters, EXPENSE_CATEGORIES } from "./types";
import { otherExpenseApi } from "./apiService";
import { formatCurrency, formatDate } from "../../utils/utils";
import { useToast } from "../../components/Toast";
import ExpenseDetailView from "./components/ExpenseDetailView";
import { Loading } from "../../components/Loading";
import NavigationHeader from "../../components/NavigationHeader";
import { smallButtonProp } from "../../utils/styles";

const NoDataView = ({ onAddClick }: { onAddClick: () => void }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      px: 2,
      backgroundColor: (theme) => theme.palette.grey[50],
      borderRadius: 2,
    }}
  >
    <ReceiptIcon
      sx={{
        fontSize: 64,
        color: (theme) => theme.palette.grey[300],
        mb: 2,
      }}
    />
    <Typography
      variant="h6"
      component="h2"
      sx={{
        color: (theme) => theme.palette.text.primary,
        mb: 1,
        textAlign: "center",
      }}
    >
      No Other Expenses Yet
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ mb: 3, textAlign: "center", maxWidth: 400 }}
    >
      Start tracking your company's other expenses by adding your first expense
      record.
    </Typography>
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={onAddClick}
      size="large"
    >
      Add First Expense
    </Button>
  </Box>
);

const OtherExpenseComponent = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<OtherExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [filters, setFilters] = useState<OtherExpenseFilters>({
    category: "",
    paidTo: "",
    startDate: "",
    endDate: "",
    sortBy: "expenseDate",
    sortOrder: "DESC",
  });

  // Detail view state
  const [selectedExpenseId, setSelectedExpenseId] = useState<string>("");
  const [detailViewOpen, setDetailViewOpen] = useState(false);

  useEffect(() => {
    fetchExpenses();
  }, [pagination.page, filters]);

  const fetchExpenses = async () => {
    try {
      setError(null);
      const response = await otherExpenseApi.getExpenses();
      setExpenses(response?.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response?.pagination?.total || 0,
        totalPages: response?.pagination?.totalPages || 0,
      }));
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setError("Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      paidTo: "",
      startDate: "",
      endDate: "",
      sortBy: "expenseDate",
      sortOrder: "DESC",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleAddExpense = () => {
    navigate("/other-expense/add");
  };

  const handleEdit = (expenseId: string) => {
    navigate(`/other-expense/add/${expenseId}`);
  };

  const handleDelete = async (expenseId: string) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      try {
        await otherExpenseApi.deleteExpense(expenseId);
        showToast("Expense deleted successfully", "success");
        fetchExpenses();
      } catch (error) {
        console.error("Error deleting expense:", error);
        showToast("Failed to delete expense", "error");
      }
    }
  };

  const handleCloseDetailView = () => {
    setDetailViewOpen(false);
    setSelectedExpenseId("");
  };

  const handleEditFromDetail = (expense: OtherExpense) => {
    navigate(`/other-expense/edit/${expense.id}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {loading && (
        <Loading fullScreen={false} message="Loading other expenses..." />
      )}
      {/* Header */}
      <NavigationHeader
        title="Other Expenses"
        button={{
          label: "Add Expense",
          onClick: handleAddExpense,
          icon: <AddIcon />,
        }}
        backNavigationEnabled={false}
      />

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {expenses.length === 0 ? (
        <NoDataView onAddClick={handleAddExpense} />
      ) : (
        <>
          {/* Expenses Table */}
          {Object.entries(
            expenses.reduce(
              (
                groups: { [key: string]: OtherExpense[] },
                expense: OtherExpense
              ) => {
                const date = formatDate(expense.expenseDate);
                if (!groups[date]) {
                  groups[date] = [];
                }
                groups[date].push(expense);
                return groups;
              },
              {}
            )
          ).map(([date, dateExpenses]: [string, OtherExpense[]]) => (
            <Card key={date} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "medium", fontSize: "0.8rem" }}
                >
                  {date}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "medium", fontSize: "0.8rem" }}
                  >
                    Total:{" "}
                    {formatCurrency(
                      dateExpenses.reduce(
                        (sum: number, exp: OtherExpense) => sum + exp.amount,
                        0
                      )
                    )}
                  </Typography>
                  <Tooltip title="Edit" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      sx={{ ...smallButtonProp.sx, ml: 1 }}
                      onClick={() =>
                        handleEdit(
                          dateExpenses[0].expenseDate?.split("T")[0] || ""
                        )
                      }
                    >
                      Edit
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small" aria-label="expense table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Location
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Balance
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Paid
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Paid To
                      </TableCell>
                      {/* <TableCell
                        sx={{ fontSize: "0.75rem", fontWeight: 600, py: 1 }}
                      >
                        Actions
                      </TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dateExpenses.map((expense: OtherExpense) => (
                      <TableRow
                        key={expense.id}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          {expense.category}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          {expense.location || "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(expense.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          {formatCurrency(expense?.balance || 0)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          {formatCurrency(expense?.totalPaid || 0)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          {expense.paidTo}
                        </TableCell>
                        {/* <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleView(expense.id)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(expense.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ))}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail View Modal */}
      <ExpenseDetailView
        open={detailViewOpen}
        onClose={handleCloseDetailView}
        expenseId={selectedExpenseId}
        onEdit={handleEditFromDetail}
      />
    </Container>
  );
};

export default OtherExpenseComponent;
